(ns app.front.view.conditions.comments
  (:require [app.domain.application.condition.model :as condition-model]
            [app.domain.party.model :as party-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.add-widget :as ui-add]
            [app.front.ui.dnd :as dnd]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [app.front.view.conditions.common :as condition-common]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

;; events
(rf/reg-event-db ::open-topic-editor
  #(assoc-in % [::checklist-topics :editor-open?] true))

(rf/reg-event-db ::open-comment-editor
  (rf/path [::checklist-comments])
  (fn [checklist-comments [_ topic-id ws]]
    (-> checklist-comments
        (assoc-in [topic-id :editor-open?] true)
        (assoc-in [topic-id :edited-fields :new :topic-id] topic-id)
        (assoc-in [topic-id :edited-fields :new :checklist.comment/circulation-round]
                  (-> ws :workspace/circulation-round (or 1))))))

(rf/reg-event-db ::set-cleared-comments
  (fn [db [_ value]]
    (assoc-in db [::checklist-comments :show-cleared-comments?] value)))

(rf/reg-event-db ::open-response-editor
  (fn [db [_ comment-id]]
    (-> db
        (assoc-in [::checklist-responses comment-id :editor-open?] true)
        (assoc-in [::checklist-responses comment-id :edited-fields :new :checklist.response/body] ""))))

(rf/reg-event-db ::toggle-reordering
  #(update-in % [::checklist-topics :reorder?] not))

(rf/reg-event-db ::close-topic-editor
  (rf/path [::checklist-topics])
  #(-> %
       (dissoc :editor-open?)
       (dissoc :loading?)
       (p/dissoc-in [:edited-fields :new])))

(rf/reg-event-db ::close-comment-editor
  (rf/path [::checklist-comments])
  (fn [comments [_ topic-id]]
    (-> comments
        (p/dissoc-in [topic-id :editor-open?])
        (p/dissoc-in [topic-id :loading?])
        (p/dissoc-in [topic-id :edited-fields :new]))))

(rf/reg-event-db ::close-response-editor
  (rf/path [::checklist-responses])
  (fn [responses [_ comment-id]]
    (-> responses
        (p/dissoc-in [comment-id :editor-open?])
        (p/dissoc-in [comment-id :loading?])
        (p/dissoc-in [comment-id :edited-fields :new]))))

(rf/reg-event-db ::edit-topic
  (fn [db [_ topic-id]]
    (update-in db [::checklist-topics :editing-ids] #(conj (set %) topic-id))))

(rf/reg-event-db ::edit-comment
  (fn [db [_ topic-id comment]]
    (-> db
        (update-in [::checklist-comments topic-id :editing-ids] #(conj (set %) (:db/id comment)))
        (assoc-in [::checklist-comments topic-id :edited-fields (:db/id comment) :checklist.comment/title] (:checklist.comment/title comment))
        (assoc-in [::checklist-comments topic-id :edited-fields (:db/id comment) :checklist.comment/body] (:checklist.comment/body comment)))))

(rf/reg-event-db ::edit-response
  (fn [db [_ comment-id response-id]]
    (update-in db [::checklist-responses comment-id :editing-ids] #(conj (set %) response-id))))

(rf/reg-event-db ::close-edit-response
  (fn [db [_ comment-id response-id]]
    (-> db
        (update-in [::checklist-responses comment-id :editing-ids] #(disj % response-id))
        (assoc-in [::checklist-responses comment-id :edited-fields response-id] {}))))

(rf/reg-event-db ::close-edit-comment
  (fn [db [_ topic-id comment-id]]
    (update-in db [::checklist-comments topic-id :editing-ids] #(disj % comment-id))))

(rf/reg-event-db ::close-edit-topic
  (fn [db [_ topic-id]]
    (-> db
        (update-in [::checklist-topics :editing-ids] #(disj % topic-id))
        (p/dissoc-in [::checklist-topics :loading?]))))

(rf/reg-event-db ::open-comment-overlay
  (fn [db [_ comment-id]]
    (assoc db ::checklist-comment-overlay comment-id)))

(rf/reg-event-db ::close-comment-overlay
  #(dissoc % ::checklist-comment-overlay))

(rf/reg-event-db ::clear-topic-loading
  #(p/dissoc-in % [::checklist-topics :loading?]))

(rf/reg-event-db ::clear-comment-loading
  (fn [db [_ topic-id]]
    (p/dissoc-in db [::checklist-comments topic-id :loading?])))

(rf/reg-event-db ::clear-response-loading
  (fn [db [_ comment-id]]
    (p/dissoc-in db [::checklist-responses comment-id :loading?])))

(rf/reg-event-db ::change-topic-field
  (fn [db [_ id field value]]
    (assoc-in db [::checklist-topics :edited-fields id field] value)))

(rf/reg-event-db ::change-comment-field
  (fn [db [_ topic-id id field value]]
    (assoc-in db [::checklist-comments topic-id :edited-fields id field] value)))

(rf/reg-event-db ::change-response-field
  (fn [db [_ comment-id id field value]]
    (assoc-in db [::checklist-responses comment-id :edited-fields id field] value)))

(rf/reg-event-fx ::create-topic
  (fn [{:keys [db]} [_ phase-id]]
    (let [topic (-> db ::checklist-topics :edited-fields :new)]
      {:db       (assoc-in db [::checklist-topics :loading?] true)
       :dispatch [:app.front.process/signal
                  phase-id
                  [:process.signal/create-checklist-topic topic]
                  {:on-success [::close-topic-editor]
                   :on-error   [::clear-topic-loading]
                   :notify     {:success :changes-saved}}]})))

(rf/reg-event-fx ::save-topic
  (fn [{:keys [db]} [_ phase-id topic-id]]
    (let [topic (-> db ::checklist-topics :edited-fields (get topic-id))]
      {:db       (assoc-in db [::checklist-topics :loading?] true)
       :dispatch [:app.front.process/signal
                  phase-id
                  [:process.signal/edit-checklist-topic (assoc topic
                                                               :topic-id topic-id)]
                  {:on-success [::close-edit-topic topic-id]
                   :notify     {:success :changes-saved}}]})))

(rf/reg-event-fx ::create-comment
  (fn [{:keys [db]} [_ phase-id topic-id]]
    (let [comment (-> db ::checklist-comments (get topic-id) :edited-fields :new)]
      {:db       (assoc-in db [::checklist-comments topic-id :loading?] true)
       :dispatch [:app.front.process/signal
                  phase-id
                  [:process.signal/create-checklist-comment comment]
                  {:on-success [::close-comment-editor topic-id]
                   :on-error   [::clear-comment-loading topic-id]
                   :notify     {:success :changes-saved}}]})))

(rf/reg-event-fx ::save-comment
  (fn [{:keys [db]} [_ phase-id topic-id comment-id]]
    (let [comment (get-in db [::checklist-comments topic-id :edited-fields comment-id])]
      {:dispatch [:app.front.process/signal
                  phase-id
                  [:process.signal/edit-checklist-comment (assoc comment
                                                                 :comment-id comment-id)]
                  {:on-success [::close-edit-comment topic-id comment-id]
                   :on-error   [::close-edit-comment topic-id comment-id]
                   :notify     {:success :changes-saved}}]})))

(rf/reg-event-fx ::save-response
  (fn [{:keys [db]} [_ phase-id comment-id response-id]]
    (let [response (get-in db [::checklist-responses comment-id :edited-fields response-id])]
      {:dispatch [:app.front.process/signal
                  phase-id
                  [:process.signal/edit-checklist-response (assoc response
                                                                  :comment-id comment-id
                                                                  :response-id response-id)]
                  {:on-success [::close-edit-response comment-id response-id]
                   :on-error   [::close-edit-response comment-id response-id]
                   :notify     {:success :changes-saved}}]})))

(rf/reg-event-fx ::edit-comment-state
  (fn [_ [_ phase-id comment-id state]]
    {:dispatch [:app.front.process/signal
                phase-id
                [:process.signal/edit-comment-state {:comment-id   comment-id
                                                     :domain/state state}]
                {:notify {:success :changes-saved}}]}))

(rf/reg-event-fx ::edit-comment-assignee
  (fn [_ [_ phase-id comment-id assignee]]
    {:dispatch [:app.front.process/signal
                phase-id
                [:process.signal/edit-comment-assignee {:comment-id comment-id :checklist.comment/assignee assignee}]
                {:notify {:success :changes-saved}}]}))

(rf/reg-event-fx ::delete-comment
  (fn [_ [_ phase-id comment-id]]
    {:dispatch-n [[:app.front.process/signal
                   phase-id
                   [:process.signal/delete-checklist-comment {:comment-id comment-id}]
                   {:notify {:success :checklist.comment/comment-delete-success}}]
                  [::close-comment-overlay]]}))

(rf/reg-event-fx ::create-response
  (fn [{:keys [db]} [_ phase-id comment-id]]
    (let [response (-> db ::checklist-responses (get comment-id) :edited-fields :new)]
      {:db       (assoc-in db [::checklist-responses comment-id :loading?] true)
       :dispatch [:app.front.process/signal
                  phase-id
                  [:process.signal/create-checklist-response
                   (assoc response :comment-id comment-id)]
                  {:on-success [::close-response-editor comment-id]
                   :on-error   [::clear-response-loading comment-id]
                   :notify     {:success :changes-saved}}]})))

(rf/reg-event-fx ::clear-all-responded-comments
  (fn [_ [_ phase-id topic-id]]
    {:dispatch [:app.front.process/signal
                phase-id
                [:process.signal/clear-all-responded-comments {:topic-id topic-id}]
                {:notify {:success :changes-saved}}]}))

;; subs
(rf/reg-sub ::checklist-topics
  #(get % ::checklist-topics))

(rf/reg-sub ::show-cleared-comments?
  :<- [::checklist-comments]
  :-> :show-cleared-comments?)

(rf/reg-sub ::checklist-comments
  #(get % ::checklist-comments))

(rf/reg-sub ::checklist-responses
  #(get % ::checklist-responses))

(rf/reg-sub ::editing-ids
  :<- [::checklist-topics]
  #(get % :editing-ids))

(rf/reg-sub ::loading-topic?
  :<- [::checklist-topics]
  #(get % :loading? false))

(rf/reg-sub ::loading-comment?
  (fn [db [_ topic-id]]
    (get-in db [::checklist-comments topic-id :loading?] false)))

(rf/reg-sub ::loading-response?
  :<- [::checklist-responses]
  (fn [responses [_ comment-id]]
    (get-in responses [comment-id :loading?] false)))

(rf/reg-sub ::loading-assignees?
  :<- [::checklist-comments]
  #(get % :loading-assignees? false))

(rf/reg-sub ::comment-overlay
  #(get % ::checklist-comment-overlay))

(rf/reg-sub ::comments
  :<- [::entities/entities {:entity/type :entity.type/checklist-comment}]
  :-> du/entities->entity-db)

(rf/reg-sub ::comment
  :<- [::comments]
  (fn [comments [_ comment-id]]
    (get comments comment-id)))

(rf/reg-sub ::comment-for-overlay
  :<- [::comments]
  :<- [::comment-overlay]
  (fn [[comments comment-id]]
    (get comments comment-id)))

(rf/reg-sub ::comment-creator
  (fn [[_ comment-id]]
    (rf/subscribe [::comment comment-id]))
  :-> :entity/creator)

(rf/reg-sub ::creating-new-comment?
  :<- [::checklist-comments]
  (fn [comments [_ topic-id]]
    (get-in comments [topic-id :edited-fields :new] false)))

(rf/reg-sub ::creating-new-response?
  :<- [::checklist-responses]
  (fn [responses [_ comment-id]]
    (get-in responses [comment-id :edited-fields :new] false)))

(rf/reg-sub ::comment-fields
  :<- [::checklist-comments]
  (fn [comments [_ topic-id id]]
    (get-in comments [topic-id :edited-fields id] {})))

(rf/reg-sub ::topics
  (fn [_ [_ application]]
    (->> application
         :entity/_parent
         (filter (eu/is-type-fn :entity.type/checklist-topic))
         (sort-by :ordered/order))))

(rf/reg-sub ::responses
  (fn [[_ comment-id]]
    (rf/subscribe [::comment comment-id]))
  (fn [comment]
    (->> (condition-model/comment-responses comment)
         du/entities->entity-db)))

(rf/reg-sub ::has-responses?
  (fn [[_ comment-id]]
    (rf/subscribe [::responses comment-id]))
  (fn [responses]
    (boolean (seq responses))))

(rf/reg-sub ::sorted-responses
  (fn [[_ comment-id]]
    (rf/subscribe [::responses comment-id]))
  (fn [responses]
    (->> responses
         vals
         (sort-by :entity/created))))

(rf/reg-sub ::creating-new-topic?
  :<- [::checklist-topics]
  :-> :editor-open?)

(rf/reg-sub ::reordering-topics?
  :<- [::checklist-topics]
  :-> :reorder?)

(rf/reg-sub ::topic-edited-fields
  :<- [::checklist-topics]
  (fn [topics [_ topic-id]]
    (get-in topics [:edited-fields topic-id])))

(rf/reg-sub ::new-topic-fields
  :<- [::checklist-topics]
  #(get-in % [:edited-fields :new] {}))

(rf/reg-sub ::response-fields
  :<- [::checklist-responses]
  (fn [responses [_ comment-id id]]
    (get-in responses [comment-id :edited-fields id] {})))

(rf/reg-sub ::response-body-edited
  (fn [[_ comment-id id]]
    (rf/subscribe [::response-fields comment-id id]))
  :-> :checklist.response/body)

(rf/reg-sub ::response-original
  (fn [[_ comment-id _]]
    (rf/subscribe [::responses comment-id]))
  (fn [responses [_ _ id]]
    (get responses id)))

(rf/reg-sub ::response-body-original
  (fn [[_ comment-id id]]
    (rf/subscribe [::response-original comment-id id]))
  :-> :checklist.response/body)

(rf/reg-sub ::body-modified?
  (fn [[_ comment-id id]]
    [(rf/subscribe [::response-body-original comment-id id])
     (rf/subscribe [::response-body-edited comment-id id])])
  (fn [[original edited]]
    (and (some? edited)
         (not= original edited))))

(rf/reg-sub ::editing-comment?
  :<- [::checklist-comments]
  (fn [comments [_ topic-id comment-id]]
    (-> comments
        (get-in [topic-id :editing-ids])
        (contains? comment-id))))

(rf/reg-sub ::editing-response?
  :<- [::checklist-responses]
  (fn [responses [_ comment-id response-id]]
    (-> responses
        (get-in [comment-id :editing-ids])
        (contains? response-id))))

(rf/reg-sub ::assignees
  :<- [::entities/context]
  (fn [_ [_ fsm-target]]
    (condition-model/available-assignees-for-comment
      fsm-target
      (-> fsm-target :entity/parent :domain/authority-group :db/id))))

(rf/reg-sub ::new-comment-save-disabled?
  (fn [[_ topic-id fsm-target]]
    [(rf/subscribe [::loading-comment? topic-id])
     (rf/subscribe [:app.front.process/signal-permitted?
                    (:db/id fsm-target)
                    [:process.signal/create-checklist-comment
                     {:topic-id topic-id}]])
     (rf/subscribe [::comment-fields topic-id :new])])
  (fn [[loading? permitted? fields] _]
    (or loading?
        (not permitted?)
        (not (get fields :checklist.comment/assignee))
        (str/blank? (get fields :checklist.comment/title))
        (str/blank? (get fields :checklist.comment/body))
        (not (get fields :checklist.comment/circulation-round)))))

;; helpers
(def editor-row :div.flex.flex-row.mt-2)

(def overlay-row :div.flex.flex-row.pl-4.pt-4.items-center.justify-between)

(defn assignee-select [{:keys [tr comment-id comment fsm-target on-change-fn hide-label? placeholder? title? light? topic-id]}]
  (let [lang (<sub [:language])]
    (if (or (and comment-id
                 (<sub [:app.front.process/signal-permitted?
                        (:db/id fsm-target)
                        [:process.signal/edit-comment-assignee
                         {:comment-id comment-id}]]))
            (and (not comment-id)
                 (<sub [:app.front.process/signal-permitted?
                        (:db/id fsm-target)
                        [:process.signal/create-checklist-comment
                         {:topic-id topic-id}]])))
      (let [assignees (<sub [::assignees fsm-target])]
        [:div.flex.flex-1.text-right
         {:on-click #(do (.stopPropagation %)
                         (.preventDefault %))}
         (when title? [:div.flex-1.mt-4.mx-2 (tr [:checklist.comment/assignee-title])])
         [:div.flex-1
          (if (<sub [::ws-subs/loading-config?])
            (cc/select-skeleton {:hide-label hide-label?})
            (into
              (cc/select (p/assoc-if {:id         "assignee"
                                      :label-text (tr [:checklist.comment/assignee])
                                      :hide-label hide-label?
                                      :light      light?
                                      :on-change  on-change-fn}
                                     :value (-> comment :checklist.comment/assignee :db/id))
                         (when placeholder? [ui/placeholder-select-item tr])
                         (futil/for-frag [assignee assignees]
                           (cc/select-item {:text  (party-model/full-name-with-roles lang assignee)
                                            :value (:db/id assignee)})))))]])
      (party-model/full-name-with-roles lang (:checklist.comment/assignee comment)))))

(defn edit-comment-field [{:keys [component tr fields topic-id comment-id light? field]}]
  [component {:label-text (tr [field])
              :id         (str "edit-comment-" topic-id "-" ((if (keyword? comment-id) name identity) comment-id) "-" (name field))
              :value      (get fields field)
              :light      light?
              :on-change  #(rf/dispatch-sync [::change-comment-field topic-id comment-id field (futil/tval %)])}])

(defn new-comment-editor [{:keys [topic-id fsm-target tr save-evt cancel-evt save-tr-key]}]
  (let [fields   (<sub [::comment-fields topic-id :new])
        loading? (<sub [::loading-comment? topic-id])
        ws       (:entity/parent fsm-target)]
    [:div.mb-8
     [ui-add/add-widget
      (util/mmap {:title   (tr [:checklist.comment/add])
                  :buttons [{:text      (tr [:cancel])
                             :kind      :ghost
                             :disabled? loading?
                             :on-click  #(>evt cancel-evt)}
                            {:text      (tr [save-tr-key])
                             :disabled? (<sub [::new-comment-save-disabled? topic-id fsm-target])
                             :on-click  #(>evt save-evt)}]}
                 loading?)
      [editor-row
       [:div.flex-1
        [ui-select/select {:id           "circulation-round"
                           :allow-empty? false
                           :values       (-> ws :workspace/circulation-round (or 1) inc range rest)
                           :label-text   (tr [:checklist.comment/round])
                           :light        true
                           :val->str     str
                           :on-change    #(>evt [::change-comment-field
                                                 topic-id
                                                 :new
                                                 :checklist.comment/circulation-round
                                                 (js/parseInt %)])
                           :value        (get fields :checklist.comment/circulation-round)}]]
       [:div.flex-1
        [assignee-select (util/mmap {:placeholder? true
                                     :hide-label?  false
                                     :light?       true
                                     :comment      fields
                                     :on-change-fn #(>evt [::change-comment-field
                                                           topic-id
                                                           :new
                                                           :checklist.comment/assignee
                                                           (when (not= (futil/tval %) "none")
                                                             (-> % futil/tval js/parseInt))])}
                                    fsm-target
                                    tr
                                    topic-id)]]]
      [editor-row
       [edit-comment-field (util/mmap {:component  ui/text-input
                                       :light?     true
                                       :comment-id :new
                                       :field      :checklist.comment/title}
                                      tr fields topic-id)]]
      [editor-row
       [edit-comment-field (util/mmap {:component  ui/text-area
                                       :light?     true
                                       :comment-id :new
                                       :field      :checklist.comment/body}
                                      tr fields topic-id)]]]]))

(defn topic-header-row [{:keys [tr idx title sub-rows reordering? topic-id editing? fields edited-fields fsm-target]}]
  (let [get-field    #(or (get edited-fields %)
                          (get fields %))
        is-editable? (<sub [:app.front.process/signal-permitted?
                            (:db/id fsm-target)
                            [:process.signal/edit-checklist-topic]])]
    [condition-common/header-row
     (util/mmap {:row-count (count sub-rows)} idx title)
     (when editing?
       [:div.flex-1.ml-1.-mt-1
        [ui/text-input {:label-text (tr [:checklist.topic/name])
                        :id         (str "topic-name-" idx)
                        :light      true
                        :hide-label true
                        :value      (get-field :checklist.topic/title)
                        :on-key-up  #(when (futil/enter-press? %)
                                       (>evt [::save-topic (:db/id fsm-target) topic-id]))
                        :on-change  #(rf/dispatch-sync [::change-topic-field topic-id :checklist.topic/title (futil/tval %)])}]])
     [:div.flex-1.text-right
      (when is-editable?
        (if editing?
          [:<>
           (cc/button {:size          :small
                       :icon          i/remove
                       :icon-position :right
                       :kind          :ghost
                       :on-click      #(>evt [::close-edit-topic topic-id])}
                      (tr [:cancel]))
           (cc/button {:size          :small
                       :disabled      (str/blank? (get-field :checklist.topic/title))
                       :icon          i/check
                       :icon-position :right
                       :kind          :primary
                       :on-click      #(>evt [::save-topic (:db/id fsm-target) topic-id])}
                      (tr [:save]))]
          [:<>
           (when (<sub [:app.front.process/signal-permitted?
                        (:db/id fsm-target)
                        [:process.signal/clear-all-responded-comments
                         {:topic-id topic-id}]])
             [ui/modal-confirmation-button
              {:id                  "delete-comment"
               :modal-class         "text-left"
               :tr                  tr
               :modal-title         (tr [:checklist.comment/clear-all-responded])
               :trigger-button-text (tr [:checklist.comment/clear-all-responded])
               :trigger-button-opts {:size :small
                                     :kind :ghost}
               :on-yes-click        #(>evt [::clear-all-responded-comments (:db/id fsm-target) topic-id])}
              (tr [:checklist.comment/clear-all-responded-info])])
           (cc/button {:size          :small
                       :icon          i/pen
                       :icon-position :right
                       :kind          :ghost
                       :on-click      #(>evt [::edit-topic topic-id])}
                      (tr [:checklist.comment/edit-topic]))]))
      (when reordering?
        (cc/icon {:class-name  "mt-2"
                  :height      "14px"
                  :icon        i/draggable
                  :description (tr [:reorder])}))]]))

(defn comment-state-change [{:keys [id state phase tr light? span-class topic-id]}]
  (let [responses?        (<sub [::has-responses? id])
        create-permitted? (<sub [:app.front.process/signal-permitted?
                                 (:db/id phase)
                                 [:process.signal/create-checklist-comment
                                  {:topic-id topic-id}]])]
    (if (<sub [:app.front.process/signal-permitted?
               (:db/id phase)
               [:process.signal/edit-comment-state
                {:comment-id id}]])
      [ui-select/select {:id           (str id "-state")
                         :data-cy      "state"
                         :allow-empty? false
                         ;; only comment without responses can be changed back to draft
                         :values       (cond ;; for non-authority only noted state possible
                                         (not create-permitted?) condition-model/applicant-editable-comment-states
                                         responses? (remove #(= % :checklist.comment.state/draft) condition-model/comment-states)
                                         :else condition-model/comment-states)
                         :hide-label   true
                         :label-text   ""
                         :light        (boolean light?)
                         :val->str     #(when %
                                          (tr [%]))
                         :on-change    #(>evt [::edit-comment-state (:db/id phase) id (keyword %)])
                         :value        state}]
      [:span.font-bold
       (when span-class
         {:class span-class})
       (tr [state])])))

(defn circulation-number [number]
  [:span.rounded-full.border-solid.border-1.border.py-1.px-2.pl-2.w-10.ml-2
   (str number ".")])

(defn comments-table [tr fsm-target topic-id comments]
  #_{:clj-kondo/ignore [:deprecated-var]}
  [ui-table/responsive-table
   {:tr                    tr
    :class                 "mb-4"
    :header-class-override "p-2 bg-white border-b border-solid font-bold"}
   [{:title (tr [:checklist.comment/title-id]) :max-width "100px"}
    {:title (tr [:checklist.comment/title-title])}
    {:title (tr [:checklist.comment/title-round]) :max-width "100px"}
    {:title (tr [:checklist.comment/title-state]) :max-width "250px"}
    {:title (tr [:checklist.comment/title-assignee]) :max-width "250px"}]
   (futil/for-all [[idx {:db/keys     [id]
                         :domain/keys [state]
                         :as          comment}] comments]
     {:key  id
      :opts {:data-cy  "comment-table"
             :class    (futil/cs "bg-gray-200 border-l-4 border-solid leading-snug hover:bg-ui-blue-10 rounded-md mt-2"
                                 (when (or (= state :checklist.comment.state/draft)
                                           (= state :checklist.comment.state/open))
                                   "border-ui-red-100")
                                 (when (= state :checklist.comment.state/cleared)
                                   "border-ui-green")
                                 (when (or (= state :checklist.comment.state/noted)
                                           (= state :checklist.comment.state/responded))
                                   "border-black"))
             :on-click #(>evt [::open-comment-overlay id])}
      :cols [{:content [:div
                        [:span.ml-2
                         (str "#" (inc idx))]]}
             {:content [:div.items-center
                        (:checklist.comment/title comment)]}
             {:content [:div
                        [circulation-number (:checklist.comment/circulation-round comment)]]}
             {:content [:div.-ml-2
                        {:on-click #(do (.stopPropagation %)
                                        (.preventDefault %))}
                        [comment-state-change (util/mmap {:phase fsm-target :light? true} id state tr topic-id)]]}
             {:content [assignee-select (util/mmap {:comment-id   id
                                                    :placeholder? false
                                                    :light?       true
                                                    :on-change-fn #(>evt [::edit-comment-assignee
                                                                          (:db/id fsm-target)
                                                                          id
                                                                          (some-> % futil/tval js/parseInt)])
                                                    :hide-label?  true}
                                                   fsm-target
                                                   comment
                                                   tr
                                                   topic-id)]}]})])

(defn topic-row-new [editing? idx topic tr reordering? &
                     [{:keys [augment-target-props augment-draggable-props]} fsm-target]]
  (let [topic-id                    (:db/id topic)
        add-comment-permitted?      (<sub [:app.front.process/signal-permitted?
                                           (:db/id fsm-target)
                                           [:process.signal/create-checklist-comment
                                            {:topic-id topic-id}]])
        comments                    (map-indexed vector (condition-model/comments-of-topic topic))
        {not-cleared-comments false
         cleared-comments     true} (group-by #(= (:domain/state (second %)) :checklist.comment.state/cleared) comments)
        creating-new-comment?       (<sub [::creating-new-comment? topic-id])
        show-cleared-comments?      (<sub [::show-cleared-comments?])]
    [:div
     (when reordering?
       (-> {:class " transform ease-in-out duration-100 cursor-pointer"}
           (augment-draggable-props topic)
           (augment-target-props topic)))
     [topic-header-row (util/mmap {:title         (:checklist.topic/title topic)
                                   :sub-rows      comments
                                   :edited-fields (<sub [::topic-edited-fields topic-id])
                                   :fields        topic
                                   :topic-id      topic-id} tr reordering? idx editing? fsm-target)]
     (when (not reordering?)
       [:<>
        (when (and (not show-cleared-comments?) (seq not-cleared-comments))
          [comments-table tr fsm-target topic-id not-cleared-comments])
        (when (and show-cleared-comments? (seq cleared-comments))
          [comments-table tr fsm-target topic-id cleared-comments])
        (if creating-new-comment?
          [new-comment-editor (util/mmap {:save-evt    [::create-comment (:db/id fsm-target) topic-id]
                                          :cancel-evt  [::close-comment-editor topic-id]
                                          :save-tr-key :checklist.comment/add}
                                         fsm-target tr topic-id)]
          [:<>
           (when add-comment-permitted?
             [:span.mr-2
              (cc/button {:size     :small
                          :on-click #(>evt [::open-comment-editor topic-id (:entity/parent fsm-target)])}
                         (tr [:checklist.comment/add]))])])])]))

(defn new-topic [fsm-target tr]
  (let [open?            (<sub [::creating-new-topic?])
        fields           (<sub [::new-topic-fields])
        loading?         (<sub [::loading-topic?])
        get-field        #(get fields %)
        button-disabled? #(or loading?
                              (not (<sub [:app.front.process/signal-permitted?
                                          (:db/id fsm-target)
                                          [%]])))]
    (when open?
      [ui-add/add-widget
       (util/mmap {:title   (tr [:checklist.topic/add])
                   :buttons [{:kind      :ghost
                              :disabled? loading?
                              :on-click  #(>evt [::close-topic-editor])
                              :text      (tr [:cancel])}
                             {:disabled? (or (button-disabled? :process.signal/create-checklist-topic)
                                             (not (get-field :checklist.topic/title)))
                              :on-click  #(>evt [::create-topic (:db/id fsm-target)])
                              :text      (tr [:checklist.topic/add])}]}
                  loading?)
       [editor-row
        [:div.flex-1.mr-4
         [ui/text-input {:label-text (tr [:checklist.topic/name])
                         :id         "new-topic-name"
                         :light      true
                         :value      (get-field :checklist.topic/title)
                         :on-key-up  #(when (futil/enter-press? %)
                                        (>evt [::create-topic (:db/id fsm-target)]))
                         :on-change  #(rf/dispatch-sync [::change-topic-field :new :checklist.topic/title (futil/tval %)])}]]]])))

(defn topics-list [{:keys [tr fsm-target topics reordering?]}]
  (let [editing-ids        (<sub [::editing-ids])
        loading-assignees? (<sub [::loading-assignees?])]
    (if loading-assignees?
      (cc/loading-spinner {:with-overlay true})
      [dnd/wrap-dnd
       {:id               "topics-list-dnd"
        :items            topics
        :props-for-source {:class "translate-x-2"}
        :on-reorder       (fn [from to]
                            (>evt [:app.front.process/signal
                                   fsm-target
                                   [:process.signal/reorder-checklist-topics
                                    {:topics (dnd/ordering-tx-for-entity from to)}]]))}
       (fn [{:keys [sorted] :as draggable-props}]
         [:div.flex.flex-col
          {:class (when-not reordering? "gap-12")}
          (futil/for-frag-indexed [[idx {:db/keys [id] :as topic}] sorted]
            [topic-row-new (contains? editing-ids id) (inc idx) topic tr reordering? draggable-props fsm-target])])])))

(defn response-body [{:keys [fields edited-fields comment-id id editing?]}]
  (if editing?
    [ui/text-area {:hide-label true
                   :value      (or (get edited-fields :checklist.response/body)
                                   (get fields :checklist.response/body))
                   :light      true
                   :class      "mt-2"
                   :on-change  #(rf/dispatch-sync [::change-response-field comment-id id :checklist.response/body (futil/tval %)])}]
    [:p.whitespace-pre-line (:checklist.response/body fields)]))

(defn new-response [phase-id tr comment-id]
  (let [creator  (<sub [::comment-creator comment-id])
        fields   (<sub [::response-fields comment-id :new])
        loading? (<sub [::loading-response? comment-id])]
    [:div.mt-2.p-8.mb-8.bg-ui-blue-10.border-2.border-solid.border-ui-blue-100.rounded-md.shadow-lg.animation-appear-slide-down
     [editor-row
      [:h4
       (cc/icon {:class-name  "mr-2"
                 :icon        i/undo
                 :description ""
                 :aria-hidden true
                 :fill        "currentcolor"})
       (tr [:checklist.response/add]
           [(user-model/full-name creator)])]]
     [editor-row
      [response-body (util/mmap {:id       :new
                                 :editing? true} fields comment-id)]]
     [:div.mt-2.text-right
      (cc/button {:size     :small
                  :kind     :ghost
                  :on-click #(>evt [::close-response-editor comment-id])}
                 (tr [:cancel]))
      (cc/button {:size     :small
                  :disabled loading?
                  :kind     :primary
                  :on-click #(>evt [::create-response phase-id comment-id])}
                 (tr [:checklist.response/send-response]))]]))

(defn response-row [{:keys                                         [phase-id tr response comment-id]
                     {:checklist.response/keys [authority?]
                      :checklist.comment/keys  [circulation-round]
                      :db/keys                 [id]
                      :entity/keys             [created creator]}  :response}]
  (let [editing?      (<sub [::editing-response? comment-id id])
        modified?     (<sub [::body-modified? comment-id id])
        edited-fields (<sub [::response-fields comment-id id])]
    [:div.rounded-md.bg-gray-100.p-4.mt-2
     {:class (futil/cs (when (not authority?) "ml-6"))}
     [:div.text-right.mb-4.font-bold (tr [:checklist.comment/title-round]) [circulation-number circulation-round]]
     [response-body (util/mmap {:fields response} edited-fields comment-id id editing?)]
     [:div.flex.mt-4
      [:div.flex-1.text-xs
       [:div.flex-row (user-model/full-name creator)]
       [:div.flex-row.mt-1 ((<sub [::date-util/date-time-fn]) created)]]
      (when (<sub [:app.front.process/signal-permitted?
                   phase-id
                   [:process.signal/edit-checklist-response
                    {:comment-id comment-id :response-id id}]])
        [:div.flex-1.text-right
         (if editing?
           [:<>
            (cc/button {:size          :small
                        :icon          i/remove
                        :icon-position :right
                        :kind          :ghost
                        :on-click      #(>evt [::close-edit-response comment-id id])}
                       (tr [:cancel]))
            (cc/button {:size          :small
                        :icon          i/check
                        :icon-position :right
                        :kind          :primary
                        :disabled      (not modified?)
                        :on-click      #(>evt [::save-response phase-id comment-id id])}
                       (tr [:save]))]
           (cc/button {:size          :small
                       :icon          i/pen
                       :icon-position :right
                       :kind          :ghost
                       :on-click      #(>evt [::edit-response comment-id id])}
                      (tr [:checklist.response/edit])))])]]))

(defn responses [{:keys [phase-id comment-id user tr]}]
  (let [responses     (<sub [::sorted-responses comment-id])
        new-response? (<sub [::creating-new-response? comment-id])
        permitted?    (<sub [:app.front.process/signal-permitted? phase-id
                             [:process.signal/create-checklist-response {:comment-id comment-id}]])]
    [:div.mt-4
     (futil/for-frag [response responses]
       [response-row (util/mmap phase-id tr response user comment-id)])
     (if new-response?
       [new-response phase-id tr comment-id]
       (when permitted?
         [:div.text-center.mt-4
          (cc/button {:size          :small
                      :icon          i/plus
                      :icon-position :right
                      :kind          :primary
                      :on-click      #(>evt [::open-response-editor comment-id])}
                     (tr [:checklist.response/new]))]))]))

(defn comments-overlay [user]
  (when-let [comment (<sub [::comment-for-overlay])]
    (let [phase      (-> comment :entity/parent :entity/parent)
          phase-id   (:db/id phase)
          tr         (<sub [:tr])
          comment-id (:db/id comment)
          topic-id   (-> comment :entity/parent :db/id)
          editing?   (<sub [::editing-comment? topic-id comment-id])
          fields     (<sub [::comment-fields topic-id comment-id])]
      [:div.fixed.top-1.right-0.bottom-0.w-full.flex.mt-14.z-30
       [:div.absolute.top-0.left-0.w-full.h-full.bg-ui-night-sky-100.opacity-25
        {:on-click #(>evt [::close-comment-overlay])}]
       [:div.absolute.top-0.right-0.h-full.flex.flex-col.bg-white.shadow-xl.animation-appear-slide-left.w-full.max-w-xl.p-4.overflow-y-scroll
        [:div.flex.flex-row.p-4
         [:div.flex-1.text-left
          [comment-state-change (util/mmap {:id         comment-id
                                            :span-class "mt-4"
                                            :state      (:domain/state comment)}
                                           phase tr topic-id)]]
         [assignee-select (util/mmap {:comment-id   (:db/id comment)
                                      :placeholder? false
                                      :light?       false
                                      :title?       true
                                      :on-change-fn #(>evt [::edit-comment-assignee
                                                            phase-id
                                                            comment-id
                                                            (some-> % futil/tval js/parseInt)])
                                      :hide-label?  true
                                      :fsm-target   phase}
                                     comment
                                     tr
                                     topic-id)]]
        [overlay-row
         (if editing?
           [edit-comment-field (util/mmap {:component ui/text-input :light? false :field :checklist.comment/title}
                                          comment-id tr fields topic-id)]
           [:h3.m-0.text-base.font-semibold (:checklist.comment/title comment)])]
        [overlay-row
         (if editing?
           [edit-comment-field (util/mmap {:component ui/text-area :light? false :field :checklist.comment/body}
                                          comment-id tr fields topic-id)]
           [:p.whitespace-pre-line (:checklist.comment/body comment)])]
        [overlay-row
         [:div.flex-1.text-xs
          [:div.flex-row (user-model/full-name (:entity/creator comment))]
          [:div.flex-row.mt-1 ((<sub [::date-util/date-time-fn]) (:entity/created comment))]]
         (if editing?
           [:div.absolute.right-5
            (cc/button {:size          :small
                        :icon          i/remove
                        :icon-position :right
                        :kind          :ghost
                        :on-click      #(>evt [::close-edit-comment topic-id comment-id])}
                       (tr [:cancel]))
            (cc/button {:size          :small
                        :icon          i/check
                        :icon-position :right
                        :kind          :primary
                        :on-click      #(>evt [::save-comment phase-id topic-id comment-id])}
                       (tr [:save]))]
           [:div.absolute.right-5
            (when (<sub [:app.front.process/signal-permitted?
                         (:db/id phase)
                         [:process.signal/delete-checklist-comment
                          {:comment-id comment-id}]])
              [ui/modal-confirmation-button
               {:id                  "delete-comment"
                :tr                  tr
                :modal-title         (tr [:checklist.comment/delete])
                :trigger-button-text (tr [:delete])
                :trigger-button-opts {:icon          i/trash
                                      :icon-position :right
                                      :size          :small
                                      :kind          :ghost}
                :on-yes-click        #(>evt [::delete-comment
                                             (:db/id phase)
                                             comment-id])}
               (tr [:checklist.comment/delete-comment-confirmation])])
            (when (<sub [:app.front.process/signal-permitted?
                         (:db/id phase)
                         [:process.signal/edit-checklist-comment
                          {:comment-id comment-id}]])
              (cc/button {:size          :small
                          :icon          i/pen
                          :icon-position :right
                          :kind          :ghost
                          :on-click      #(>evt [::edit-comment topic-id comment])}
                         (tr [:edit])))])]
        [responses (util/mmap phase-id comment-id user tr)]]])))
